import Fuse from "fuse.js";
import cloneDeep from "lodash/cloneDeep";
import isEmpty from "lodash/isEmpty";
import {
  type ChangeEventHandler,
  type RefObject,
  createRef,
  useEffect,
  useRef,
  useState,
} from "react";
import type { SelectItemRenderer, SelectRenderer } from "react-dropdown-select";
import Select from "react-dropdown-select";

import ExternalAttendeeIcon from "@assets/icons/external-attendee.svg";
import CloseRoundedIcon from "@assets/icons/close-icon-rounded.svg";
import SearchIcon from "@assets/icons/search.svg";

import useUserStore from "@store/useUserStore";
import { MIN_DROPDOWN_MENU_HEIGHT } from "@components/AddNewEntity/hooks/useMaxMenuHeight";
import { User } from "@app-types/common.type";
import { IPeople } from "@interfaces/people.interface";
import { Checkbox, FormLabel } from "@ui/FormElements";
import {
  getUserSplitNameFromFullName,
  normalizeString,
  validateMultiFormatEmail,
} from "@utils/strings.util";
import { cn } from "@utils/tailwind.util";
import { CompanyAvatar, CompanyAvatarSizes, LetterAvatar } from "@ui/Avatars";
import useSystemStore from "@store/useSystemStore";
import { APP_COLORS } from "@styles/colors";

export type UserWithSelectAll = User & {
  isSelectAllOption?: boolean;
  isSectionHeader?: boolean;
  type?: string | null;
};

export interface ITeamSelectorProps {
  value: User[];
  options: UserWithSelectAll[];
  preselectedUsers?: (User | IPeople)[];
  size?: "medium" | "small";
  placeholder: string;
  noDataText?: string;
  label?: string;
  canAddExternalMembers?: boolean;
  currentAttendees?: User[];
  onChange: (value: (User | IPeople)[]) => void;
  setSearchValue: (value: string) => void;
  searchValue: string;
  disabled?: boolean;
  isResetPreselectedMembers?: boolean;
  hideSelectAllOption?: boolean;
  keepDropdownOpen?: boolean;
  // Bottom edge of this element marks the top of the dropdown's usable area
  // (typically the panel chrome / sticky header). Falls back to viewport top.
  headerRef?: RefObject<HTMLElement>;
  // Top edge of this element marks the bottom of the dropdown's usable area
  // (typically the form's sticky footer). Falls back to viewport when omitted.
  footerRef?: RefObject<HTMLElement>;
}

const DROPDOWN_BOUNDARY_GAP = 8;
const MAX_DROPDOWN_MENU_HEIGHT = 400;

export const isPersonItem = (item: User | IPeople): item is IPeople =>
  !!item && "companies" in item;

function ContentRender({
  ref,
  placeholder,
  value,
  onChange,
  onClear,
  methods,
}: {
  ref: React.LegacyRef<HTMLDivElement>;
  placeholder: string;
  value: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
  onClear: VoidFunction;
} & SelectRenderer<User>) {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      e.stopPropagation();
    }
  };

  return (
    <div ref={ref} className="flex w-full items-center gap-1.5">
      <SearchIcon
        width={20}
        height={20}
        className="text-content-secondary dark:text-invertedPrimary"
      />
      <div className="flex h-5 w-full items-center">
        <input
          value={value}
          className="w-full border-0 bg-transparent p-0 text-sm !ring-0 placeholder:text-[#9CA1AF]"
          onFocus={() => methods.dropDown("open")}
          placeholder={placeholder}
          onChange={onChange}
          onKeyDown={handleKeyDown}
        />
        {value && (
          <button
            type="button"
            className="absolute p-0 top-0 right-4 opacity-70 duration-200 hover:opacity-100 flex items-center h-full"
            onMouseDown={(e) => e.preventDefault()} // Stop native scrolling
            onClick={onClear}
            aria-label="Clear search"
          >
            <CloseRoundedIcon
              width={16}
              height={16}
              color="#6B7080"
              className="inline-block align-middle"
            />
          </button>
        )}
      </div>
    </div>
  );
}

function ItemRender({
  item,
  itemIndex,
  props,
  state,
  methods,
  areAllMembersSelected,
  selectedMembers,
  selectAll,
  unselectMembers,
  options,
  userData,
  value,
  isDarkMode,
}: SelectItemRenderer<UserWithSelectAll> & {
  areAllMembersSelected: boolean;
  selectedMembers: User[];
  selectAll: () => void;
  unselectMembers: () => void;
  options: UserWithSelectAll[];
  userData: User;
  value: UserWithSelectAll[];
  isDarkMode: boolean;
}) {
  const itemRef = createRef<HTMLDivElement>();
  const isFocused = state.cursor === itemIndex;
  const needsRenderEmail = isEmpty(item?.first_name) && isEmpty(item?.last_name);
  const isItemSelected = selectedMembers.some((member) => member.id === item.id);

  const isPeople = isPersonItem(item) ? Boolean(item.companies) : false;

  const selectedText = isDarkMode ? APP_COLORS.text.primaryDark : APP_COLORS.text.primary;
  const hoverBg = isDarkMode
    ? APP_COLORS.background.secondaryDark
    : APP_COLORS.background.secondary;

  const handleSelectAll = () => {
    methods.clearAll();
    selectAll();
  };

  const handleUnselectMembers = () => {
    methods.clearAll();
    unselectMembers();
  };

  const addItemsSequentially = async (items: User[]) => {
    for (const item of items) {
      await new Promise((resolve) => {
        methods.addItem(item);
        setTimeout(resolve, 10);
      });
    }
  };

  const handleClick = () => {
    if (props.disabled) return;

    if (isItemSelected) {
      methods.removeItem(null, item, false);
    } else {
      const newItems = [];
      if (item.id !== userData.id && value.length === 0) {
        const userMe = options.find((member) => member.id === userData.id);

        if (userMe) {
          newItems.push(userMe);
        }
      }

      newItems.push(item);

      void addItemsSequentially(newItems);

      if (item.external && item.email === item.id) {
        methods.dropDown("close");
      }
    }
  };

  if (isFocused) {
    /* 20ms wait time until itemRef is available.
      we are using timeout here since this inside component doesn't support hooks like useEffect.
    */
    setTimeout(() => {
      itemRef.current?.scrollIntoView({ behavior: "instant", block: "nearest", inline: "start" });
    }, 20);
  }

  if (item.isSelectAllOption) {
    return (
      <div
        ref={itemRef}
        tabIndex={0}
        key={itemIndex}
        className={cn("flex min-h-[36px] items-center gap-2 pl-3", {
          "bg-background-alternative": isFocused,
        })}
        onClick={() => (areAllMembersSelected ? handleUnselectMembers() : handleSelectAll())}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = hoverBg;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "";
        }}
      >
        <Checkbox
          parentClassName="flex-shrink-0"
          className={cn("mr-1 cursor-pointer border-stroke-primary bg-transparent", {
            "!text-action-primary": areAllMembersSelected,
          })}
          checked={areAllMembersSelected}
          variant="small"
          labelClassName="cursor-pointer"
          readOnly
        />
        <p className="select-none text-sm font-normal" style={{ color: selectedText }}>
          Select Full Team
        </p>
      </div>
    );
  } else {
    return (
      <>
        <div
          ref={itemRef}
          tabIndex={0}
          key={itemIndex}
          className={cn("min-w-0 flex flex-1 items-center gap-2 p-2 pl-3", {
            "bg-background-alternative": isFocused,
            "min-h-[36px]": !isPeople,
          })}
          onClick={handleClick}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = hoverBg;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "";
          }}
        >
          <Checkbox
            parentClassName="flex-shrink-0"
            className={cn("mr-1 cursor-pointer border-stroke-primary bg-transparent", {
              "!text-action-primary": isItemSelected,
            })}
            checked={isItemSelected}
            variant="small"
            labelClassName="cursor-pointer"
            readOnly
          />

          {item.external && (
            <div className="-mx-1 flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-background-alternative">
              <ExternalAttendeeIcon />
            </div>
          )}

          <div className="flex-shrink-0">
            {!item.external ? (
              isPeople && isPersonItem(item) && item.companies.length > 0 ? (
                <CompanyAvatar
                  classNames="w-7 h-7 border rounded-full"
                  size={CompanyAvatarSizes.BIG}
                  logoUrl={item?.primary_company?.company?.logo_url}
                  userProps={item}
                />
              ) : (
                <LetterAvatar
                  sizeClassName={`${
                    isPersonItem(item) && item.companies && item.companies.length === 0
                      ? "w-7 h-7 !mx-0"
                      : "w-5 h-5"
                  }`}
                  user={item}
                />
              )
            ) : null}
          </div>

          {needsRenderEmail && (
            <span
              className="text-ellipsis overflow-hidden whitespace-nowrap text-sm font-medium"
              style={{ color: selectedText }}
            >
              {item.email}
            </span>
          )}

          {!needsRenderEmail && (
            <>
              {isPeople ? (
                <div className="flex select-none flex-col gap-1">
                  <span
                    className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium"
                    style={{ color: selectedText }}
                  >
                    {item.first_name} {item.last_name}
                  </span>
                  <div className="overflow-hidden text-ellipsis whitespace-nowrap text-xs text-gray-450">
                    {isPersonItem(item) && item.primary_company ? (
                      <span>{item.primary_company.company.title}</span>
                    ) : null}

                    {isPersonItem(item) && item.primary_company ? (
                      item.primary_company.relation && item.primary_company.relation.length > 0 ? (
                        <span className="ml-1">• {item.primary_company.relation}</span>
                      ) : null
                    ) : null}
                  </div>
                  <a
                    href={`mailto:${item.email}`}
                    className="block overflow-hidden text-ellipsis whitespace-nowrap text-xs text-teal-600 underline"
                  >
                    {item.email}
                  </a>
                </div>
              ) : (
                <span
                  className="text-ellipsis select-none overflow-hidden whitespace-nowrap text-sm font-medium"
                  style={{ color: selectedText }}
                >
                  {item.first_name} {item.last_name}
                </span>
              )}
            </>
          )}
          {item.title && (
            <span className="min-w-10 text-ellipsis overflow-hidden whitespace-nowrap text-sm text-content-secondary">
              {item.title.title}
            </span>
          )}
        </div>
        {item.external && (
          <div className="w-full rounded-md bg-[#F9F9FB] p-4 text-sm font-medium text-gray-450">
            A new person will be added with this email
          </div>
        )}
      </>
    );
  }
}

function NoDataRender(noDataText: string | undefined) {
  return (
    <div className="flex items-center">
      <span className="p-3 text-sm text-content-secondary">
        {noDataText ? noDataText : "No team member found."}
      </span>
    </div>
  );
}

export function TeamSelector({
  value,
  options,
  preselectedUsers,
  placeholder,
  size = "medium",
  onChange,
  noDataText,
  label,
  canAddExternalMembers,
  currentAttendees,
  setSearchValue,
  searchValue,
  disabled = false,
  isResetPreselectedMembers,
  hideSelectAllOption = false,
  keepDropdownOpen = false,
  headerRef,
  footerRef,
}: ITeamSelectorProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const selectorRef = useRef<HTMLDivElement>(null);
  const [maxMenuHeight, setMaxMenuHeight] = useState(400);
  const [filteredOptions, setFilteredOptions] = useState<UserWithSelectAll[]>(options);
  const [isPreselectedMembers, setIsPreselectedMembers] = useState(false);
  const [hasManualSelections, setHasManualSelections] = useState(false);

  const { activeUser } = useUserStore();
  const { isDarkMode } = useSystemStore();

  const membersFromOptions = options.filter((item) => !("companies" in item));

  const areAllMembersSelected = membersFromOptions.every((item) =>
    value.some((member) => member.id === item.id)
  );

  const handleInternalChange = (members: User[]) => {
    setSearchValue("");
    setHasManualSelections(true);
    onChange(members);
  };

  const handleSelectAll = () => {
    const selectedPeople = value.filter((person) => "companies" in person);
    const allMembers = options.filter(
      (member) => !("companies" in member) && !member.isSectionHeader && !member.isSelectAllOption
    );

    let membersToReturn = [...allMembers, ...selectedPeople];

    /* Validate if exist some external members to add to the final array. */
    if (canAddExternalMembers) {
      const externalUsers = value.filter((member) => member.external === true);
      membersToReturn = membersToReturn.concat(externalUsers);
    }

    setSearchValue("");
    setHasManualSelections(true);
    onChange(membersToReturn);
  };

  const unselectMembers = () => {
    const selectedPeople = value.filter((person) => "companies" in person);
    const membersToReturn = [...selectedPeople];

    setSearchValue("");
    setHasManualSelections(true);
    onChange(membersToReturn);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      contentRef.current?.click();
    }

    if (event.key === "Escape") {
      event.stopPropagation();
      setSearchValue("");
    }
  };

  useEffect(() => {
    // Only apply preselection if no manual selections have been made
    if (
      !preselectedUsers ||
      preselectedUsers.length === 0 ||
      isPreselectedMembers ||
      hasManualSelections
    )
      return;

    if (preselectedUsers && preselectedUsers.length > 0) {
      onChange(preselectedUsers);
      setIsPreselectedMembers(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedUsers, options]);

  useEffect(() => {
    // Only update based on preselected users if no manual selections have been made
    if (isPreselectedMembers && !hasManualSelections) {
      if (preselectedUsers && preselectedUsers.length > 0) {
        onChange(preselectedUsers);
      }
    }
  }, [options, preselectedUsers, value, isPreselectedMembers, onChange, hasManualSelections]);

  useEffect(() => {
    if (isResetPreselectedMembers) {
      setIsPreselectedMembers(false);
      setHasManualSelections(false);
    }
  }, [isResetPreselectedMembers]);

  // Use the caller-supplied footer ref as the bottom boundary of the dropdown
  // (falls back to viewport). Combined with `dropdownPosition="auto"`, the menu
  // uses the larger of space-above / space-below and flips when below is tight.
  useEffect(() => {
    function update() {
      if (!selectorRef.current) return;
      const rect = selectorRef.current.getBoundingClientRect();
      const topBoundary = headerRef?.current ? headerRef.current.getBoundingClientRect().bottom : 0;
      const bottomBoundary = footerRef?.current
        ? footerRef.current.getBoundingClientRect().top
        : window.innerHeight;
      const spaceAbove = rect.top - topBoundary - DROPDOWN_BOUNDARY_GAP;
      const spaceBelow = bottomBoundary - rect.bottom - DROPDOWN_BOUNDARY_GAP;
      const available = Math.max(spaceBelow, spaceAbove, MIN_DROPDOWN_MENU_HEIGHT);
      setMaxMenuHeight(Math.min(available, MAX_DROPDOWN_MENU_HEIGHT));
    }
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [footerRef, headerRef]);

  /* Logic to add external attendee option if the search value contains valid email information. */
  useEffect(() => {
    if (canAddExternalMembers) {
      const externalAttendeeData = validateMultiFormatEmail(searchValue);
      const externalAttendeeSplitName = getUserSplitNameFromFullName(
        externalAttendeeData?.fullName
      );
      const searchEmailInOptionsIndex = options.findIndex(
        (option) =>
          (option.email ?? "").toLowerCase() === (externalAttendeeData?.email ?? "").toLowerCase()
      );

      const currentAttendeesDataIndex = currentAttendees?.findIndex(
        (option) =>
          (option.email ?? "").toLowerCase() === (externalAttendeeData?.email ?? "").toLowerCase()
      );

      if (
        externalAttendeeData !== null &&
        searchEmailInOptionsIndex === -1 &&
        currentAttendeesDataIndex === -1
      ) {
        const newfilteredOptions = cloneDeep(filteredOptions);
        const index = newfilteredOptions.findIndex(
          (option) => option.email === externalAttendeeData.email
        );

        const externalAttendee = {
          id: externalAttendeeData.email ?? "",
          email: externalAttendeeData.email ?? "",
          full_name: externalAttendeeData.fullName,
          first_name: externalAttendeeSplitName?.firstName,
          last_name: externalAttendeeSplitName?.lastName,
          external: true,
          title: null,
          timezone: "",
        };

        if (index === -1) {
          newfilteredOptions.unshift(externalAttendee);
          setFilteredOptions(newfilteredOptions);
        }
      }
    }
  }, [canAddExternalMembers, currentAttendees, filteredOptions, options, searchValue]);

  useEffect(() => {
    const selectAllOption = {
      id: "selectAllOption",
      full_name: "Select Full Team",
      isSelectAllOption: true,
    } as UserWithSelectAll;

    const teamMembersOptions = options.filter((opt) => opt.type !== "people");
    const peopleOptions = options.filter((opt) => opt.type === "people");

    let filteredPeople = peopleOptions;
    let filteredTeamMembers = teamMembersOptions;

    if (searchValue.length > 0) {
      const fusePeople = new Fuse(peopleOptions, {
        includeScore: true,
        ignoreLocation: true,
        threshold: 0.2,
        keys: ["full_name", "email"],
      });

      const fuseTeamMembers = new Fuse(teamMembersOptions, {
        includeScore: true,
        ignoreLocation: true,
        threshold: 0.2,
        keys: ["full_name", "email"],
      });

      const parsedSearchValue = normalizeString(searchValue);

      filteredPeople = fusePeople.search(parsedSearchValue).map(({ item }) => item);
      filteredTeamMembers = fuseTeamMembers.search(parsedSearchValue).map(({ item }) => item);
    }

    const result: UserWithSelectAll[] = [];

    if (filteredTeamMembers.length > 0) {
      result.push({
        id: "team-header",
        full_name: "TEAM MEMBERS",
        isSectionHeader: true,
      } as UserWithSelectAll);

      if (searchValue.length === 0 && !hideSelectAllOption) {
        result.push(selectAllOption);
      }

      result.push(...filteredTeamMembers);
    }

    if (filteredPeople.length > 0) {
      result.push({
        id: "people-header",
        full_name: "PEOPLE",
        isSectionHeader: true,
      } as UserWithSelectAll);
      result.push(...filteredPeople);
    }

    setFilteredOptions(result);
  }, [options, searchValue, hideSelectAllOption]);

  return (
    <div ref={selectorRef} className="flex flex-col" onKeyDown={handleKeyDown}>
      {label && <FormLabel>{label}</FormLabel>}
      <Select
        multi
        className={cn(
          "!rounded-lg !border-gray-200 dark:border-gray-600 bg-white dark:text-white dark:bg-gray-850",
          "hover:!border-[#D1D3DB] dark:!border-gray-600",
          "focus:!border-stroke-actionPrimary focus:!shadow-mainButtonFocus",
          "focus-within:!shadow-[0_0_0_0_#000000]",
          { "!pl-3.5 !pr-2": size === "medium" },
          { "!pl-2 !pr-1": size === "small" },
          { "!cursor-not-allowed !bg-background-alternative": disabled }
        )}
        contentRenderer={(props) =>
          ContentRender({
            ref: contentRef,
            placeholder,
            value: searchValue,
            onChange: (event) => setSearchValue(event.target.value),
            onClear: () => setSearchValue(""),
            ...props,
          })
        }
        disabled={disabled}
        dropdownHandle={false}
        dropdownPosition="auto"
        itemRenderer={(props) => {
          if ((props.item as UserWithSelectAll).isSectionHeader) {
            return (
              <div className="px-2 pb-1 pt-2 text-[13px] font-medium uppercase text-gray-450">
                {props.item.full_name}
              </div>
            );
          }

          return ItemRender({
            value,
            options,
            areAllMembersSelected,
            selectedMembers: value,
            selectAll: handleSelectAll,
            unselectMembers,
            userData: activeUser as User,
            isDarkMode,
            ...props,
          });
        }}
        noDataRenderer={() => NoDataRender(noDataText)}
        onChange={(members) => handleInternalChange(members)}
        options={filteredOptions}
        searchable={true}
        style={{ minHeight: size === "medium" ? 36 : 28 }}
        values={value}
        valueField="id"
        keepOpen={keepDropdownOpen}
        dropdownHeight={maxMenuHeight ? `${maxMenuHeight}px` : undefined}
      />
    </div>
  );
}
